import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { 
    ShoppingBag, IndianRupee, Clock, CheckCircle, Percent, Save, Check, 
    AlertCircle, Calendar, Sliders, Settings, X, Layers, Megaphone, 
    Laptop, GraduationCap, MapPin, RefreshCw, PlusCircle, ShieldCheck 
} from 'lucide-react';

export default function FranchiseTransactions() {
    const toast = useToast();
    const [transactions, setTransactions] = useState([]);
    const [branches, setBranches] = useState([]);
    const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [totalSales, setTotalSales] = useState(0);
    const [totalCommission, setTotalCommission] = useState(0);
    const [totalCollected, setTotalCollected] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [royaltyPercentage, setRoyaltyPercentage] = useState(5);
    const [editingRoyalty, setEditingRoyalty] = useState(5);
    const [savingRoyalty, setSavingRoyalty] = useState(false);
    const [collectingId, setCollectingId] = useState(null);
    const [markingAll, setMarkingAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [fetchingData, setFetchingData] = useState(false);
    const [showFeeModal, setShowFeeModal] = useState(false);
    const [savingFeeConfig, setSavingFeeConfig] = useState(false);
    const [feeConfig, setFeeConfig] = useState({
        royalty_percentage: 5,
        fixed_monthly_fee: 0,
        platform_subscription_fee: 0,
        brand_fee: 0,
        marketing_fund_percentage: 0,
        technology_fee: 0,
        support_training_fee: 0,
        territory_fee: 0,
        renewal_fee: 0,
        new_outlet_fee: 0
    });

    const fetchTransactions = async (monthFilter = selectedMonth, branchFilter = selectedBranch) => {
        setFetchingData(true);
        try {
            const params = {};
            if (monthFilter && monthFilter !== 'all') {
                params.selectedMonth = monthFilter;
            }
            if (branchFilter && branchFilter !== '' && branchFilter !== 'all') {
                params.restaurantId = branchFilter;
            }
            const res = await api.get('/api/franchises/stats', { params });
            setTransactions(res.data.recentOrders || []);
            if (res.data.branchBreakdown && res.data.branchBreakdown.length > 0) {
                setBranches(res.data.branchBreakdown);
            }
            setMonthlyBreakdown(res.data.monthlyBreakdown || []);
            setTotalSales(res.data.totalSales || 0);
            setTotalCommission(res.data.totalCommissionEarned || 0);
            setTotalCollected(res.data.totalCommissionCollected || 0);
            setTotalPending(res.data.totalCommissionPending || 0);
            if (res.data.feeConfig) {
                setFeeConfig(res.data.feeConfig);
                if (res.data.feeConfig.royalty_percentage !== undefined) {
                    setRoyaltyPercentage(res.data.feeConfig.royalty_percentage);
                    setEditingRoyalty(res.data.feeConfig.royalty_percentage);
                }
            } else {
                const rate = res.data.defaultRoyaltyPercentage || 5;
                setRoyaltyPercentage(rate);
                setEditingRoyalty(rate);
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to load transaction logs');
        } finally {
            setLoading(false);
            setFetchingData(false);
        }
    };

    const handleSaveFeeConfig = async () => {
        setSavingFeeConfig(true);
        try {
            const res = await api.put('/api/franchises/fee-config', { feeConfig });
            toast.success('Franchise fee structure updated successfully!');
            if (res.data.feeConfig) {
                setFeeConfig(res.data.feeConfig);
                setRoyaltyPercentage(res.data.feeConfig.royalty_percentage);
                setEditingRoyalty(res.data.feeConfig.royalty_percentage);
            }
            setShowFeeModal(false);
            fetchTransactions();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to save fee configuration');
        } finally {
            setSavingFeeConfig(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    const handleSaveRoyalty = async () => {
        setSavingRoyalty(true);
        try {
            await api.put('/api/franchises/royalty', { royaltyPercentage: editingRoyalty });
            toast.success(`Franchise royalty percentage updated to ${editingRoyalty}%`);
            fetchTransactions(selectedMonth, selectedBranch);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to update royalty percentage');
        } finally {
            setSavingRoyalty(false);
        }
    };

    const handleToggleCollected = async (orderId, currentStatus) => {
        setCollectingId(orderId);
        const nextStatus = currentStatus === 'collected' ? 'pending' : 'collected';
        try {
            await api.put(`/api/franchises/orders/${orderId}/collect-royalty`, { status: nextStatus });
            toast.success(`Royalty status updated to ${nextStatus.toUpperCase()}`);
            fetchTransactions(selectedMonth, selectedBranch);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to update collection status');
        } finally {
            setCollectingId(null);
        }
    };

    const handleMarkAllCollected = async () => {
        setMarkingAll(true);
        try {
            const res = await api.put('/api/franchises/orders/collect-all-royalty', {
                restaurantId: selectedBranch || null
            });
            toast.success(`Successfully marked ${res.data.count || 0} order royalties as collected!`);
            fetchTransactions(selectedMonth, selectedBranch);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to batch collect royalties');
        } finally {
            setMarkingAll(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    const filteredTransactions = selectedBranch
        ? transactions.filter(t => String(t.restaurant_id) === String(selectedBranch))
        : transactions;

    const filteredCommissionTotal = filteredTransactions
        .filter(t => t.status !== 'cancelled')
        .reduce((sum, t) => sum + (t.commission_amount || 0), 0);

    const filteredCommissionCollected = filteredTransactions
        .filter(t => t.status !== 'cancelled' && t.royalty_status === 'collected')
        .reduce((sum, t) => sum + (t.commission_amount || 0), 0);

    const filteredCommissionPending = filteredTransactions
        .filter(t => t.status !== 'cancelled' && (t.royalty_status === 'pending' || !t.royalty_status))
        .reduce((sum, t) => sum + (t.commission_amount || 0), 0);

    const filteredSalesTotal = filteredTransactions
        .filter(t => t.status !== 'cancelled')
        .reduce((sum, t) => sum + parseFloat(t.final_total_amount || 0), 0);

    return (
        <div className="space-y-8 pb-20">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Consolidated Transactions & Royalty</h1>
                    <p className="text-slate-500 text-xs sm:text-sm mt-1">Real-time purchase logs, franchise commission cuts, and royalty settlement tracking.</p>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                    {fetchingData && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-xl text-xs font-bold border border-primary-200 animate-pulse shrink-0">
                            <div className="h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                            <span>Updating...</span>
                        </div>
                    )}

                    <button
                        onClick={handleMarkAllCollected}
                        disabled={markingAll}
                        className="px-3.5 py-2 bg-emerald-600 text-white font-bold text-xs rounded-xl hover:bg-emerald-700 transition-all shadow-md shadow-emerald-200 disabled:opacity-50 flex items-center gap-1.5 shrink-0 whitespace-nowrap"
                    >
                        {markingAll ? (
                            <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <CheckCircle className="w-4 h-4" />
                                <span>Collect All Pending</span>
                            </>
                        )}
                    </button>

                    <button
                        onClick={() => setShowFeeModal(true)}
                        className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all shadow-md flex items-center gap-1.5 shrink-0 whitespace-nowrap"
                    >
                        <Sliders className="w-4 h-4 text-amber-400" />
                        <span>Configure Fee Engine</span>
                    </button>

                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-600 whitespace-nowrap">Month:</label>
                        <select
                            value={selectedMonth}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedMonth(val);
                                fetchTransactions(val, selectedBranch);
                            }}
                            className="bg-white border border-slate-200 text-slate-700 text-xs rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm font-medium"
                        >
                            <option value="all">All Months</option>
                            {monthlyBreakdown.map(m => (
                                <option key={m.month_key} value={m.month_key}>{m.month_label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-600 whitespace-nowrap">Branch:</label>
                        <select
                            value={selectedBranch}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedBranch(val);
                                fetchTransactions(selectedMonth, val);
                            }}
                            className="bg-white border border-slate-200 text-slate-700 text-xs rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-sm font-medium"
                        >
                            <option value="">All Branches</option>
                            {branches.map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Metric Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-2">
                    <div className="flex justify-between items-center text-slate-400">
                        <span className="text-xs font-bold uppercase tracking-wider">Gross Sales</span>
                        <div className="bg-primary-50 p-2 rounded-xl text-primary-600">
                            <IndianRupee className="w-5 h-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-black text-slate-900">₹{(selectedBranch ? filteredSalesTotal : totalSales).toFixed(2)}</p>
                    <p className="text-xs text-slate-400">Total revenue across non-cancelled orders</p>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-2">
                    <div className="flex justify-between items-center text-slate-400">
                        <span className="text-xs font-bold uppercase tracking-wider">Royalty Earned</span>
                        <div className="bg-indigo-50 p-2 rounded-xl text-indigo-600">
                            <Percent className="w-5 h-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-black text-indigo-600">₹{(selectedBranch ? filteredCommissionTotal : totalCommission).toFixed(2)}</p>
                    <p className="text-xs text-slate-400">Gross commission cut across orders</p>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-2">
                    <div className="flex justify-between items-center text-slate-400">
                        <span className="text-xs font-bold uppercase tracking-wider">Royalty Collected</span>
                        <div className="bg-emerald-50 p-2 rounded-xl text-emerald-600">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-black text-emerald-600">₹{(selectedBranch ? filteredCommissionCollected : totalCollected).toFixed(2)}</p>
                    <p className="text-xs text-slate-400">Amount settled & collected</p>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-2">
                    <div className="flex justify-between items-center text-slate-400">
                        <span className="text-xs font-bold uppercase tracking-wider">Pending Collection</span>
                        <div className="bg-amber-50 p-2 rounded-xl text-amber-600">
                            <AlertCircle className="w-5 h-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-black text-amber-600">₹{(selectedBranch ? filteredCommissionPending : totalPending).toFixed(2)}</p>
                    <p className="text-xs text-slate-400">Unsettled balance due from branches</p>
                </div>
            </div>

            {/* Active Configured Franchise Fee Structure Overview */}
            <div className="bg-slate-900 text-white p-6 rounded-3xl border border-slate-800 shadow-xl space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-2xl border border-amber-500/30">
                            <Sliders className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-black tracking-tight text-white">Active Franchise Fee & Collection Schedule</h3>
                            <p className="text-xs text-slate-400">Configured collection terms for all branch outlets.</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowFeeModal(true)}
                        className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs rounded-xl transition-all shadow-md flex items-center gap-1.5 shrink-0"
                    >
                        <Settings className="w-4 h-4" />
                        <span>Customize Charges</span>
                    </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-2">
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Royalty Fee</span>
                        <span className="text-sm font-black text-amber-400">{feeConfig.royalty_percentage}%</span>
                        <span className="text-[10px] text-slate-500 block">of gross sales</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Fixed Franchise Fee</span>
                        <span className="text-sm font-black text-emerald-400">₹{(feeConfig.fixed_monthly_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">per month</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Platform Subscription</span>
                        <span className="text-sm font-black text-indigo-400">₹{(feeConfig.platform_subscription_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">per month / outlet</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Brand & IP Fee</span>
                        <span className="text-sm font-black text-sky-400">₹{(feeConfig.brand_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">per month</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Marketing Contribution</span>
                        <span className="text-sm font-black text-purple-400">{feeConfig.marketing_fund_percentage}%</span>
                        <span className="text-[10px] text-slate-500 block">of sales</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Technology Fee</span>
                        <span className="text-sm font-black text-teal-400">₹{(feeConfig.technology_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">per month</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Support & Training</span>
                        <span className="text-sm font-black text-pink-400">₹{(feeConfig.support_training_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">per month</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Territory Rights</span>
                        <span className="text-sm font-black text-rose-400">₹{(feeConfig.territory_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">annual / territory</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Agreement Renewal</span>
                        <span className="text-sm font-black text-cyan-400">₹{(feeConfig.renewal_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">per renewal term</span>
                    </div>
                    <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">New Outlet Fee</span>
                        <span className="text-sm font-black text-orange-400">₹{(feeConfig.new_outlet_fee || 0).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-500 block">one-time / outlet</span>
                    </div>
                </div>
            </div>

            {/* Month-Wise Royalty Statement Section */}
            {monthlyBreakdown.length > 0 && (
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">Month-Wise Royalty Statement</h3>
                            <p className="text-xs text-slate-500">Monthly breakdown of gross sales, earned commissions, and collection settlement status.</p>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[10px] tracking-wider border-b border-slate-100">
                                <tr>
                                    <th className="px-4 py-3">Month</th>
                                    <th className="px-4 py-3">Total Orders</th>
                                    <th className="px-4 py-3">Gross Sales</th>
                                    <th className="px-4 py-3">Royalty Earned</th>
                                    <th className="px-4 py-3">Royalty Collected</th>
                                    <th className="px-4 py-3">Pending Settlement</th>
                                    <th className="px-4 py-3 text-right">Settlement Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-medium">
                                {monthlyBreakdown.map((m) => (
                                    <tr 
                                        key={m.month_key} 
                                        onClick={() => {
                                            const nextVal = selectedMonth === m.month_key ? 'all' : m.month_key;
                                            setSelectedMonth(nextVal);
                                            fetchTransactions(nextVal, selectedBranch);
                                        }}
                                        className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${selectedMonth === m.month_key ? 'bg-primary-50/40 font-bold' : ''}`}
                                    >
                                        <td className="px-4 py-3 text-slate-900 font-bold flex items-center gap-2">
                                            <Calendar className="w-3.5 h-3.5 text-primary-600 shrink-0" />
                                            <span>{m.month_label}</span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-700">{m.order_count} order{m.order_count !== 1 ? 's' : ''}</td>
                                        <td className="px-4 py-3 font-semibold text-slate-900">₹{m.total_sales.toFixed(2)}</td>
                                        <td className="px-4 py-3 font-bold text-indigo-600">₹{m.total_commission_earned.toFixed(2)}</td>
                                        <td className="px-4 py-3 font-bold text-emerald-600">₹{m.total_commission_collected.toFixed(2)}</td>
                                        <td className="px-4 py-3 font-bold text-amber-600">₹{m.total_commission_pending.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right">
                                            {m.total_commission_pending === 0 ? (
                                                <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">Fully Settled</span>
                                            ) : (
                                                <span className="bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">Pending Settlement</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Default Royalty Editor Bar */}
            <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="bg-slate-100 p-2 rounded-xl text-slate-700">
                        <Percent className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-slate-900">Default Franchise Royalty Rate</h4>
                        <p className="text-xs text-slate-400">Standard commission percentage applied to all branch orders.</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <div className="relative w-32">
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={editingRoyalty}
                            onChange={(e) => setEditingRoyalty(e.target.value)}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-primary-500 pr-8"
                        />
                        <span className="absolute inset-y-0 right-3 flex items-center text-slate-400 text-xs font-bold pointer-events-none">%</span>
                    </div>
                    <button
                        onClick={handleSaveRoyalty}
                        disabled={savingRoyalty || parseFloat(editingRoyalty) === parseFloat(royaltyPercentage)}
                        className="px-5 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 disabled:opacity-40 transition-all flex items-center gap-1.5 shrink-0"
                    >
                        {savingRoyalty ? (
                            <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <Save className="w-3.5 h-3.5" />
                                <span>Save Rate</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-slate-100 text-xs">
                        <thead className="bg-slate-50/40">
                            <tr>
                                <th className="px-4 py-3 text-left font-bold text-slate-400 uppercase tracking-wider text-[10px]">Order ID</th>
                                <th className="px-4 py-3 text-left font-bold text-slate-400 uppercase tracking-wider text-[10px]">Branch Location</th>
                                <th className="px-4 py-3 text-left font-bold text-slate-400 uppercase tracking-wider text-[10px]">Order Total</th>
                                <th className="px-4 py-3 text-left font-bold text-slate-400 uppercase tracking-wider text-[10px]">Franchise Cut</th>
                                <th className="px-4 py-3 text-left font-bold text-slate-400 uppercase tracking-wider text-[10px]">Royalty Status</th>
                                <th className="px-4 py-3 text-left font-bold text-slate-400 uppercase tracking-wider text-[10px]">Date & Time</th>
                                <th className="px-4 py-3 text-left font-bold text-slate-400 uppercase tracking-wider text-[10px]">Order Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-100 font-medium">
                            {filteredTransactions.map(order => (
                                <tr key={order.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 font-mono">#{order.id}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-800 font-semibold">{order.branch_name}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-900 font-bold">₹{parseFloat(order.final_total_amount || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-1">
                                            <span className="text-emerald-600 font-bold">₹{(order.commission_amount || 0).toFixed(2)}</span>
                                            <span className="text-[10px] text-slate-400 font-medium">({order.royalty_percentage || 5}%)</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {order.royalty_status === 'collected' ? (
                                            <button
                                                onClick={() => handleToggleCollected(order.id, 'collected')}
                                                disabled={collectingId === order.id}
                                                className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 hover:bg-amber-100 hover:text-amber-800 transition-colors rounded-full text-[10px] font-extrabold uppercase flex items-center gap-1 group"
                                                title="Click to mark back as Pending"
                                            >
                                                <Check className="w-3 h-3 text-emerald-600 group-hover:hidden" />
                                                <span className="group-hover:hidden">Collected</span>
                                                <span className="hidden group-hover:inline">Mark Pending</span>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleToggleCollected(order.id, 'pending')}
                                                disabled={collectingId === order.id}
                                                className="px-2.5 py-0.5 bg-amber-500 text-white hover:bg-emerald-600 transition-all rounded-full text-[10px] font-bold uppercase shadow-sm flex items-center gap-1 disabled:opacity-50"
                                            >
                                                {collectingId === order.id ? (
                                                    <div className="h-2.5 w-2.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <>
                                                        <CheckCircle className="w-3 h-3" />
                                                        <span>Mark Collected</span>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-450">
                                        <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> {new Date(order.created_at).toLocaleString()}</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                            order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                                        }`}>
                                            {order.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {filteredTransactions.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-slate-400 italic">
                                        No transactions recorded across brand branches yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Fee Configuration Modal */}
            {showFeeModal && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-3xl max-w-4xl w-full border border-slate-200 shadow-2xl overflow-hidden my-8 animate-scale-up">
                        <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-2xl border border-amber-500/30">
                                    <Sliders className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black">Franchise Fee & Royalty Configurator</h2>
                                    <p className="text-xs text-slate-400">Customize charge types and revenue parameters collected from your branch outlets.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowFeeModal(false)}
                                className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6 text-left">
                            {/* 1. Percentage-based Fees */}
                            <div className="space-y-4">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <Percent className="w-3.5 h-3.5 text-primary-500" />
                                    Sales Percentage Fees
                                </h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <div className="flex justify-between items-center">
                                            <label className="text-xs font-bold text-slate-800">Royalty Fee</label>
                                            <span className="text-[10px] text-slate-400 font-semibold">% of monthly gross sales</span>
                                        </div>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="100"
                                                value={feeConfig.royalty_percentage}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, royalty_percentage: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                            <span className="absolute right-3 top-3 text-xs font-bold text-slate-400">%</span>
                                        </div>
                                        <p className="text-[11px] text-slate-500">Standard commission on gross outlet sales (e.g. 5%)</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <div className="flex justify-between items-center">
                                            <label className="text-xs font-bold text-slate-800">Marketing Fund</label>
                                            <span className="text-[10px] text-slate-400 font-semibold">% of monthly gross sales</span>
                                        </div>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="100"
                                                value={feeConfig.marketing_fund_percentage}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, marketing_fund_percentage: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                            <span className="absolute right-3 top-3 text-xs font-bold text-slate-400">%</span>
                                        </div>
                                        <p className="text-[11px] text-slate-500">Common advertising & brand marketing contribution (e.g. 2%)</p>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Fixed Recurring Monthly Fees */}
                            <div className="space-y-4 pt-2 border-t border-slate-100">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                                    Fixed Recurring Monthly Fees
                                </h3>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">Fixed Monthly Franchise Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.fixed_monthly_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, fixed_monthly_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">Flat recurring fee per month (e.g. ₹10,000)</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">Platform Subscription</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.platform_subscription_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, platform_subscription_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">Portal usage per outlet location (e.g. ₹2,000/mo)</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">Brand & Trademark Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.brand_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, brand_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">Use of brand name & IP (e.g. ₹5,000/mo)</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">Technology Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.technology_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, technology_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">POS, app & reporting integration (e.g. ₹1,500/mo)</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">Support & Training Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.support_training_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, support_training_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">Ongoing staff training & support (e.g. ₹3,000/mo)</p>
                                    </div>
                                </div>
                            </div>

                            {/* 3. One-Time & Agreement Fees */}
                            <div className="space-y-4 pt-2 border-t border-slate-100">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                                    Agreement & One-Time Fees
                                </h3>
                                <div className="grid sm:grid-cols-3 gap-4">
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">Territory Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.territory_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, territory_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">Exclusive area rights payment (e.g. ₹50,000)</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">Renewal Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.renewal_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, renewal_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">Franchise agreement renewal fee (e.g. Every 5 yrs)</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                        <label className="text-xs font-bold text-slate-800">New Outlet Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={feeConfig.new_outlet_fee}
                                                onChange={(e) => setFeeConfig({ ...feeConfig, new_outlet_fee: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white border border-slate-300 rounded-xl pl-7 pr-4 py-2.5 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500">Charged per new branch setup (e.g. ₹25,000)</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                            <button
                                onClick={() => setShowFeeModal(false)}
                                className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-bold text-xs rounded-xl hover:bg-slate-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveFeeConfig}
                                disabled={savingFeeConfig}
                                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
                            >
                                {savingFeeConfig ? (
                                    <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 text-emerald-400" />
                                        <span>Save Fee Structure</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
